import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useParams, Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  resolveUrl,
  translateStoreCategory,
  localizedStoreProductName,
  isStoreArabicLang,
} from "../../utils/storeUtils";
import { useTranslation } from "react-i18next";
import { useStoreCart } from "./StoreCartContext";
import { ProductCard, QuickViewModal, CategoryTreeFilter, CategoryBreadcrumbs } from "./components";
import StoreGrainientBackdrop from "./components/StoreGrainientBackdrop";
import { StorePriceDisplay } from "./components/StorePriceDisplay";
import { buildBreadcrumb, collectDescendantIds, indexCategoryTree } from "../../utils/categoryTreeUtils";
import { trackEvent } from "../../utils/trackEvent";
import Seo from "../../components/Seo/Seo";
import "./Store.css";
import "./StoreCategory.css";

function categoryLabelToSlug(label) {
  return String(label || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}


function normalizeCategoryKey(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[\s-]+/g, "");
}

/** Comma-separated product _id values from collection scene links. */
function parseSceneProductIdsParam(raw) {
  const q = String(raw || "").trim();
  if (!q) return [];
  const out = [];
  const seen = new Set();
  q.split(",").forEach((part) => {
    const id = String(part || "").trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
}

/** Match product.category to a category label (e.g. "Tables", "Armchairs") */
function matchProductToCategory(product, categoryLabel) {
  if (!categoryLabel) return false;
  const cat = normalizeCategoryKey(product?.category);
  const slug = normalizeCategoryKey(categoryLabel);
  // Collapse "Armchairs" into "Chairs"
  if (slug === "chairs" || slug === "chair") {
    return cat === "chairs" || cat === "chair" || cat === "armchairs" || cat === "armchair";
  }
  if (slug === "doors" || slug === "door") {
    const fireDoor = cat.includes("fire") && (cat.includes("door") || cat.includes("rated"));
    if (fireDoor) return false;
    return cat === "doors" || cat === "door" || (cat.includes("door") && !cat.includes("fire"));
  }
  if (slug === "firerateddoors") {
    return cat.includes("fire") && cat.includes("door");
  }
  return cat === slug || cat.includes(slug);
}

function resolveCategoryAsset(url, publicBase) {
  if (!url || typeof url !== "string") return "";
  const raw = String(url).trim();
  if (!raw) return "";

  // First, let the shared resolver normalize `/uploads/*` to the correct host.
  // This protects against DB rows that saved absolute `http://<ip>/uploads/...` behind a TLS-terminating proxy.
  let resolved = raw;
  try {
    // No `apiBaseUrl` here (this helper is module-scoped).
    // `resolveUrl` will derive the correct origin from `window.location.origin` when running in the browser.
    resolved = resolveUrl(raw);
  } catch {
    resolved = raw;
  }

  // If the CMS provided an absolute `http://` URL for our own domain, upgrade it to https to avoid mixed-content.
  if (/^http:\/\//i.test(resolved)) {
    try {
      const parsed = new URL(resolved);
      const host = parsed.hostname.toLowerCase();
      const isLocal =
        host === "localhost" ||
        host === "127.0.0.1" ||
        host === "::1" ||
        host.endsWith(".local");
      const currentHost =
        typeof window !== "undefined" && window.location
          ? window.location.hostname.toLowerCase()
          : "";
      const isSameSite =
        (currentHost && host === currentHost) ||
        host === "alramlaa.com" ||
        host === "www.alramlaa.com";
      if (!isLocal && isSameSite) {
        return resolved.replace(/^http:\/\//i, "https://");
      }
    } catch {
      // If URL parsing fails, leave as-is.
    }
  }

  if (/^https?:\/\//i.test(resolved)) return resolved;
  if (resolved.startsWith("/")) return `${publicBase}${resolved}`;
  return `${publicBase}/${resolved}`.replace(/([^:]\/)\/+/g, "$1");
}

/** Slug from URL to display label (e.g. "tables" -> "Tables") */
function slugToLabel(slug) {
  if (!slug || typeof slug !== "string") return "";
  return slug.charAt(0).toUpperCase() + slug.slice(1).toLowerCase();
}

const SORT_OPTIONS = [
  { value: "default" },
  { value: "rating" },
  { value: "price-asc" },
  { value: "price-desc" },
  { value: "name" },
];

export default function StoreCategory() {
  const { t, i18n } = useTranslation("global");
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const searchQuery = (searchParams.get("q") || "").trim();
  const productIdsParam = searchParams.get("productIds") ?? "";
  const sceneProductIdsOrdered = useMemo(
    () => parseSceneProductIdsParam(productIdsParam),
    [productIdsParam]
  );
  const sceneProductIdSet = useMemo(
    () => new Set(sceneProductIdsOrdered),
    [sceneProductIdsOrdered]
  );
  const hasSceneProductFilter = sceneProductIdSet.size > 0;
  const { addToCart: addToCartContext, toggleWishlist, isInWishlist } = useStoreCart();
  const publicBase = process.env.PUBLIC_URL || "";
  const [storeCategories, setStoreCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [includedCategoryIds, setIncludedCategoryIds] = useState([]);
  const apiBaseUrl = axios.defaults.baseURL || "";
  const resolveProductUrl = useCallback(
    (url) => resolveUrl(url, apiBaseUrl),
    [apiBaseUrl]
  );

  const categoryNavItems = useMemo(() => {
    const source = storeCategories.length > 0 ? storeCategories : [];
    const items = [...source]
      .sort((a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0))
      .map((item) => ({
        label: item.label,
        slug: item.slug || categoryLabelToSlug(item.label),
        // Only ROOT categories should show icons in UI.
        iconSvg: item?.parentId ? "" : resolveCategoryAsset(item.iconUrl, publicBase),
        imageUrl: resolveCategoryAsset(item.coverImageUrl || item.imageUrl, publicBase),
      }));
    const filteredItems = items.filter((c) => {
      const s = String(c.slug || "").toLowerCase();
      return true;
    });
    const cover =
      filteredItems.find((c) => c.imageUrl)?.imageUrl ||
      resolveCategoryAsset("/photos/wd-furniture-pt-sofas-opt.webp", publicBase);
    const allNav = {
      label: t("store.categories.all", { defaultValue: "All items" }),
      slug: "all",
      iconSvg: "",
      icon: "fa-table-cells-large",
      imageUrl: cover,
    };
    return [allNav, ...filteredItems.filter((c) => (c.slug || "").toLowerCase() !== "all")];
  }, [storeCategories, publicBase, t]);

  const isAllCategory = (categorySlug || "").toLowerCase() === "all";

  const categoryLabel = useMemo(() => {
    const slug = (categorySlug || "").toLowerCase();
    if (slug === "all") return "";
    const found = categoryNavItems.find((item) => item.slug === slug);
    return found?.label || slugToLabel(categorySlug || "");
  }, [categoryNavItems, categorySlug]);

  const categoryTitle = useMemo(() => {
    if (isAllCategory) {
      return t("store.allCategoryTitle", { defaultValue: "All products" });
    }
    return translateStoreCategory(t, { slug: categorySlug, label: categoryLabel });
  }, [t, categorySlug, categoryLabel, isAllCategory]);

  const heroImage = useMemo(() => {
    const slug = (categorySlug || "").toLowerCase();
    if (slug === "all") {
      return categoryNavItems.find((c) => c.slug !== "all" && c.imageUrl)?.imageUrl || null;
    }
    const found = categoryNavItems.find((item) => item.slug === slug);
    return found?.imageUrl || null;
  }, [categoryNavItems, categorySlug]);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(10000);
  const [selectedColors, setSelectedColors] = useState([]);
  const [colorSearch, setColorSearch] = useState("");
  const [materialsSearch, setMaterialsSearch] = useState("");
  const [sortBy, setSortBy] = useState("default");
  const sortOptionLabel = useCallback(
    (value) => {
      switch (String(value || "")) {
        case "rating":
          return t("store.sortRating", { defaultValue: "Sort by average rating" });
        case "price-asc":
          return t("store.sortPriceAsc", { defaultValue: "Price: low to high" });
        case "price-desc":
          return t("store.sortPriceDesc", { defaultValue: "Price: high to low" });
        case "name":
          return t("store.sortNameAsc", { defaultValue: "Name A–Z" });
        case "default":
        default:
          return t("store.sortDefault", { defaultValue: "Default" });
      }
    },
    [t]
  );

  const [viewMode, setViewMode] = useState("grid");
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(24);
  const loadMoreRef = React.useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [productsResponse, categoriesResponse] = await Promise.all([
          axios.get("/store/products"),
          axios.get("/store/categories"),
        ]);
        setProducts(Array.isArray(productsResponse.data) ? productsResponse.data : []);
        setStoreCategories(
          Array.isArray(categoriesResponse.data) ? categoriesResponse.data : []
        );
        axios
          .get("/store/categories/tree")
          .then(({ data }) => setCategoryTree(Array.isArray(data) ? data : []))
          .catch(() => setCategoryTree([]));
      } catch (error) {
        toast.error(t("store.failedToLoadProducts", { defaultValue: "Failed to load products." }));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const treeIndex = useMemo(() => indexCategoryTree(categoryTree), [categoryTree]);

  // Keep selection in sync with route slug (for deep links / refresh)
  useEffect(() => {
    const slug = String(categorySlug || "").toLowerCase();
    if (slug === "armchairs" || slug === "armchair") {
      navigate("/store/category/chairs", { replace: true });
      return;
    }
    if (!slug || slug === "all") {
      setSelectedCategoryId("");
      setIncludedCategoryIds([]);
      return;
    }
    const node = treeIndex.bySlug.get(slug);
    if (!node) return;
    const ids = collectDescendantIds(node);
    setSelectedCategoryId(String(node._id));
    setIncludedCategoryIds(ids);
    trackEvent("category_view", { categorySlug: slug, categoryName: node.label || slug });
  }, [categorySlug, treeIndex]);

  const categoryProducts = useMemo(() => {
    let list = products;
    if (!isAllCategory && includedCategoryIds.length > 0) {
      const set = new Set(includedCategoryIds);
      list = list.filter((p) => {
        const cid = p?.categoryId ? String(p.categoryId) : "";
        if (cid && set.has(cid)) return true;
        // Backward compatibility: only fall back to label matching when product has no categoryId yet.
        if (!cid) return categoryLabel ? matchProductToCategory(p, categoryLabel) : false;
        return false;
      });
    } else if (!isAllCategory) {
      list = categoryLabel ? products.filter((p) => matchProductToCategory(p, categoryLabel)) : [];
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((p) => {
        const name = (p?.name || "").toLowerCase();
        const arName = (p?.arName || "").toLowerCase();
        const category = (p?.category || "").toLowerCase();
        const brand = (p?.brand || "").toLowerCase();
        const materials = (p?.materials || "").toLowerCase();
        return (
          name.includes(q) ||
          arName.includes(q) ||
          category.includes(q) ||
          brand.includes(q) ||
          materials.includes(q)
        );
      });
    }
    if (hasSceneProductFilter) {
      list = list.filter((p) => p?._id != null && sceneProductIdSet.has(String(p._id)));
    }
    return list;
  }, [
    products,
    categoryLabel,
    searchQuery,
    isAllCategory,
    includedCategoryIds,
    hasSceneProductFilter,
    sceneProductIdSet,
  ]);

  const activeTreeNode = useMemo(() => {
    if (!selectedCategoryId) return null;
    return treeIndex.byId.get(String(selectedCategoryId)) || null;
  }, [selectedCategoryId, treeIndex]);

  const seoTitle = activeTreeNode?.seoTitle || categoryTitle;
  const seoDescription =
    activeTreeNode?.seoDescription ||
    `تسوّق ${categoryTitle} من الرملاء للصناعات الخشبية — تصاميم عالية الجودة وتوصيل لجميع مناطق السعودية.`;

  const breadcrumbs = useMemo(
    () => buildBreadcrumb(treeIndex, activeTreeNode),
    [treeIndex, activeTreeNode]
  );

  const { priceBounds, colorsWithCount, materialsWithCount } = useMemo(() => {
    const prices = categoryProducts.map((p) => Number(p?.price ?? 0)).filter((n) => n >= 0);
    const minP = prices.length ? Math.min(...prices) : 0;
    const maxP = prices.length ? Math.max(...prices) : 10000;
    const colorCounts = {};
    categoryProducts.forEach((p) => {
      (p?.colors || []).forEach((c) => {
        const name = (c?.name || "").trim();
        if (name) {
          if (!colorCounts[name]) colorCounts[name] = { count: 0, hex: c?.hex || "" };
          colorCounts[name].count += 1;
        }
      });
    });
    const materialsCounts = {};
    categoryProducts.forEach((p) => {
      const raw = (p?.materials || "").trim();
      if (raw) {
        const list = raw.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
        list.forEach((name) => {
          materialsCounts[name] = (materialsCounts[name] || 0) + 1;
        });
      }
    });
    return {
      priceBounds: [minP, maxP],
      colorsWithCount: Object.entries(colorCounts).map(([name, { count, hex }]) => ({ name, count, hex })),
      materialsWithCount: Object.entries(materialsCounts).map(([name, count]) => ({ name, count })),
    };
  }, [categoryProducts]);

  useEffect(() => {
    const [minP, maxP] = priceBounds;
    setPriceMin(minP);
    setPriceMax(maxP);
  }, [priceBounds]);

  const applyPriceFilter = useCallback(() => {
    const [minP, maxP] = priceBounds;
    setPriceMin((prev) => Math.min(Math.max(prev, minP), maxP));
    setPriceMax((prev) => Math.min(Math.max(prev, minP), maxP));
    setVisibleCount(24);
  }, [priceBounds]);

  const filteredProducts = useMemo(() => {
    const [minB, maxB] = priceBounds;
    const low = Math.max(priceMin, minB);
    const high = Math.min(priceMax, maxB);
    return categoryProducts.filter((p) => {
      const price = Number(p?.price ?? 0);
      if (price < low || price > high) return false;
      if (selectedColors.length > 0) {
        const names = (p?.colors || []).map((c) => (c?.name || "").trim()).filter(Boolean);
        if (!names.some((n) => selectedColors.includes(n))) return false;
      }
      if (selectedMaterials.length > 0) {
        const raw = (p?.materials || "").trim();
        const list = raw ? raw.split(/[,;]/).map((s) => s.trim()).filter(Boolean) : [];
        if (!list.some((m) => selectedMaterials.includes(m))) return false;
      }
      return true;
    });
  }, [categoryProducts, priceBounds, priceMin, priceMax, selectedColors, selectedMaterials]);

  const sortedProducts = useMemo(() => {
    const list = [...filteredProducts];
    if (sortBy === "rating") {
      list.sort((a, b) => (Number(b?.rating ?? 0) - Number(a?.rating ?? 0)));
    } else if (sortBy === "price-asc") {
      list.sort((a, b) => Number(a?.price ?? 0) - Number(b?.price ?? 0));
    } else if (sortBy === "price-desc") {
      list.sort((a, b) => Number(b?.price ?? 0) - Number(a?.price ?? 0));
    } else if (sortBy === "name") {
      const lang = i18n.language || "en";
      const loc = isStoreArabicLang(lang) ? "ar" : "en";
      list.sort((a, b) =>
        localizedStoreProductName(a, lang).localeCompare(
          localizedStoreProductName(b, lang),
          loc,
          { sensitivity: "base" }
        )
      );
    }
    if (hasSceneProductFilter && sceneProductIdsOrdered.length > 0 && sortBy === "default") {
      const order = new Map(sceneProductIdsOrdered.map((id, i) => [id, i]));
      list.sort(
        (a, b) =>
          (order.get(String(a._id)) ?? Number.MAX_SAFE_INTEGER) -
          (order.get(String(b._id)) ?? Number.MAX_SAFE_INTEGER)
      );
    }
    return list;
  }, [filteredProducts, sortBy, i18n.language, hasSceneProductFilter, sceneProductIdsOrdered]);

  const totalResults = sortedProducts.length;
  const visibleProducts = useMemo(
    () => sortedProducts.slice(0, Math.max(0, visibleCount)),
    [sortedProducts, visibleCount]
  );

  // Reset infinite scroll when query changes (filters/sort/category)
  useEffect(() => {
    setVisibleCount(24);
  }, [
    sortBy,
    viewMode,
    categorySlug,
    searchQuery,
    selectedColors.length,
    selectedMaterials.length,
    priceMin,
    priceMax,
    includedCategoryIds.join(","),
    sceneProductIdsOrdered.join(","),
  ]);

  const clearSceneProductFilter = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("productIds");
    const qs = next.toString();
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : "" }, { replace: true });
  }, [navigate, location.pathname, searchParams]);

  const toggleColor = useCallback((name) => {
    setSelectedColors((prev) =>
      prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]
    );
    setVisibleCount(24);
  }, []);

  const toggleMaterial = useCallback((name) => {
    setSelectedMaterials((prev) =>
      prev.includes(name) ? prev.filter((m) => m !== name) : [...prev, name]
    );
    setVisibleCount(24);
  }, []);

  const filteredColors = useMemo(() => {
    const q = (colorSearch || "").toLowerCase().trim();
    if (!q) return colorsWithCount;
    return colorsWithCount.filter((c) => c.name.toLowerCase().includes(q));
  }, [colorsWithCount, colorSearch]);

  const filteredMaterials = useMemo(() => {
    const q = (materialsSearch || "").toLowerCase().trim();
    if (!q) return materialsWithCount;
    return materialsWithCount.filter((m) => m.name.toLowerCase().includes(q));
  }, [materialsWithCount, materialsSearch]);

  const activeFilterCount = useMemo(() => {
    const [lowB, highB] = priceBounds;
    let n = 0;
    if (priceMin > lowB || priceMax < highB) n += 1;
    n += selectedColors.length;
    n += selectedMaterials.length;
    if (hasSceneProductFilter) n += 1;
    return n;
  }, [priceBounds, priceMin, priceMax, selectedColors, selectedMaterials, hasSceneProductFilter]);

  // Infinite scroll sentinel
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return undefined;
    if (loading) return undefined;
    if (visibleCount >= totalResults) return undefined;

    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        setVisibleCount((prev) => Math.min(totalResults, prev + 24));
      },
      { root: null, rootMargin: "800px 0px", threshold: 0 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [loading, totalResults, visibleCount]);

  useEffect(() => {
    if (!mobileFiltersOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileFiltersOpen]);

  useEffect(() => {
    if (!mobileFiltersOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setMobileFiltersOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileFiltersOpen]);

  const addToCart = useCallback(
    (product, options) => {
      addToCartContext(product, options);
      const label = localizedStoreProductName(product, i18n.language);
      toast.success(
        label
          ? t("store.toastProductAddedToCart", {
              name: label,
              defaultValue: "{{name}} added to cart.",
            })
          : t("store.toastProductAddedToCartGeneric", {
              defaultValue: "Product added to cart.",
            })
      );
    },
    [addToCartContext, i18n.language, t]
  );

  if (!categorySlug) {
    return (
      <div className="store-category-page">
        <StoreGrainientBackdrop />
        <div className="store-category-content">
          <p>Category not found.</p>
          <Link to="/store" className="store-checkout-back">
            <i className="fa-solid fa-arrow-left" /> Back to store
          </Link>
        </div>
      </div>
    );
  }

  const categoryFilterSections = (
    <>
      {Array.isArray(categoryTree) && categoryTree.length > 0 ? (
        <div className="store-category-filter-section store-category-filter-section--tree">
          <CategoryTreeFilter
            tree={categoryTree}
            selectedCategoryId={selectedCategoryId}
            onChange={({ selectedId, includedIds, selectedNode }) => {
              setSelectedCategoryId(selectedId || "");
              setIncludedCategoryIds(Array.isArray(includedIds) ? includedIds : []);
              setVisibleCount(24);
              // When the user clears the category, show "All products".
              if (!selectedNode?.slug) {
                if ((categorySlug || "").toLowerCase() !== "all") {
                  navigate("/store/category/all");
                }
                return;
              }
              const slug = String(selectedNode.slug).toLowerCase();
              if (slug && slug !== (categorySlug || "").toLowerCase()) {
                navigate(`/store/category/${slug}`);
              }
            }}
          />
        </div>
      ) : null}
      <div className="store-category-filter-section">
        <h3 className="store-category-filter-title">
          {t("store.filterByPrice", { defaultValue: "Filter By Price" })}
        </h3>
        <div className="store-category-price-range">
          <label className="store-category-range-row">
            <span className="store-category-range-label">
              {t("store.min", { defaultValue: "Min" })}
            </span>
            <input
              type="range"
              min={priceBounds[0]}
              max={priceBounds[1]}
              step={Math.max(1, (priceBounds[1] - priceBounds[0]) / 100)}
              value={priceMin}
              onChange={(e) => {
                const v = Number(e.target.value);
                setPriceMin(Math.min(v, priceMax));
                setVisibleCount(24);
              }}
              className="store-category-range"
              aria-label={t("store.minPriceAria", { defaultValue: "Minimum price" })}
            />
          </label>
          <label className="store-category-range-row">
            <span className="store-category-range-label">
              {t("store.max", { defaultValue: "Max" })}
            </span>
            <input
              type="range"
              min={priceBounds[0]}
              max={priceBounds[1]}
              step={Math.max(1, (priceBounds[1] - priceBounds[0]) / 100)}
              value={priceMax}
              onChange={(e) => {
                const v = Number(e.target.value);
                setPriceMax(Math.max(v, priceMin));
                setVisibleCount(24);
              }}
              className="store-category-range"
              aria-label={t("store.maxPriceAria", { defaultValue: "Maximum price" })}
            />
          </label>
        </div>
        <div className="store-category-price-row">
          <p className="store-category-price-label">
            {t("store.filterPriceRangeLabel", { defaultValue: "Price:" })}{" "}
            <StorePriceDisplay amount={priceMin} fractionDigits={0} />
            <span aria-hidden="true"> – </span>
            <StorePriceDisplay amount={priceMax} fractionDigits={0} />
          </p>
          <button
            type="button"
            className="store-category-filter-btn"
            onClick={applyPriceFilter}
          >
            {t("store.applyFilter", { defaultValue: "Filter" })}
          </button>
        </div>
      </div>

      <div className="store-category-filter-section">
        <h3 className="store-category-filter-title">
          {t("store.color", { defaultValue: "Color" })}
        </h3>
        <div className="store-category-filter-search">
          <input
            type="search"
            placeholder={t("store.findAColor", { defaultValue: "Find a Color" })}
            value={colorSearch}
            onChange={(e) => setColorSearch(e.target.value)}
            className="store-category-filter-input"
            aria-label={t("store.searchColorsAria", { defaultValue: "Search colors" })}
          />
          <i className="fa-solid fa-magnifying-glass store-category-filter-search-icon" aria-hidden />
        </div>
        <ul className="store-category-filter-list store-category-filter-list--colors">
          {filteredColors.length === 0 ? (
            <li className="store-category-filter-empty">
              {t("store.noColors", { defaultValue: "No colors" })}
            </li>
          ) : (
            filteredColors.map(({ name, count, hex }) => (
              <li key={name}>
                <button
                  type="button"
                  className={`store-category-filter-option store-category-filter-option--color${selectedColors.includes(name) ? " is-selected" : ""}`}
                  onClick={() => toggleColor(name)}
                >
                  <span
                    className="store-category-filter-color-swatch"
                    style={{ background: hex || "#ccc" }}
                    aria-hidden
                  />
                  <span className="store-category-filter-option-name">{name}</span>
                  <span className="store-category-filter-count">{count}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      </div>

      <div className="store-category-filter-section">
        <h3 className="store-category-filter-title">
          {t("store.materials", { defaultValue: "Materials" })}
        </h3>
        <div className="store-category-filter-search">
          <input
            type="search"
            placeholder={t("store.findAMaterial", { defaultValue: "Find a Material" })}
            value={materialsSearch}
            onChange={(e) => setMaterialsSearch(e.target.value)}
            className="store-category-filter-input"
            aria-label={t("store.searchMaterialsAria", { defaultValue: "Search materials" })}
          />
          <i className="fa-solid fa-magnifying-glass store-category-filter-search-icon" aria-hidden />
        </div>
        <ul className="store-category-filter-list">
          {filteredMaterials.length === 0 ? (
            <li className="store-category-filter-empty">
              {t("store.noMaterials", { defaultValue: "No materials" })}
            </li>
          ) : (
            filteredMaterials.map(({ name, count }) => (
              <li key={name}>
                <button
                  type="button"
                  className={`store-category-filter-option${selectedMaterials.includes(name) ? " is-selected" : ""}`}
                  onClick={() => toggleMaterial(name)}
                >
                  <span className="store-category-filter-option-name">{name}</span>
                  <span className="store-category-filter-count">{count}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </>
  );

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={isAllCategory ? "/store" : `/store/category/${categorySlug}`}
        image={heroImage || undefined}
      />
    <div className="store-category-page">
      <StoreGrainientBackdrop />
      <section className="store-category-view" aria-labelledby="store-category-heading">
        <h1 id="store-category-heading" className="store-category-hero-title-sr">
          {categoryTitle}
        </h1>
        <div className="store-category-hero store-category-hero--photo">
          {heroImage ? (
            <div
              className="store-category-hero-photo"
              style={{ backgroundImage: `url("${heroImage}")` }}
              role="img"
              aria-label={t("store.categoryHeroAria", {
                title: categoryTitle,
                defaultValue: "{{title}} collection",
              })}
            >
              <span className="store-category-hero-label">{categoryTitle}</span>
              <CategoryBreadcrumbs crumbs={breadcrumbs} />
            </div>
          ) : (
            <p className="store-category-hero-title--fallback">{categoryTitle}</p>
          )}
        </div>

        <div className="store-category-content store-category-content--with-sidebar">
          <aside
            className="store-category-sidebar store-category-sidebar--desktop"
            aria-label={t("store.filtersSidebarLabel", { defaultValue: "Filter products" })}
          >
            {categoryFilterSections}
          </aside>

          <div className="store-category-main">
            {hasSceneProductFilter ? (
              <div className="store-category-scene-banner" role="status">
                <p className="store-category-scene-banner-text">
                  {t("store.sceneProductsBanner", {
                    count: sceneProductIdsOrdered.length,
                    defaultValue:
                      "Showing products from this photo. You can add them to your cart or keep filtering.",
                  })}
                </p>
                <button
                  type="button"
                  className="store-category-scene-banner-clear"
                  onClick={clearSceneProductFilter}
                >
                  {t("store.sceneProductsClear", { defaultValue: "Show all products" })}
                </button>
              </div>
            ) : null}
            <div className="store-category-results-bar">
              <p className="store-category-results-text">
                {loading
                  ? t("store.loading", { defaultValue: "Loading…" })
                  : t("store.showingResultsOfTotal", {
                      shown: Math.min(visibleProducts.length, totalResults),
                      total: totalResults,
                      defaultValue: `Showing ${Math.min(visibleProducts.length, totalResults)} of ${totalResults} results`,
                    })}
              </p>
              <div className="store-category-results-controls">
                <div className="store-category-view-toggle" role="group" aria-label="View mode">
                  <button
                    type="button"
                    className={`store-category-view-btn${viewMode === "list" ? " is-active" : ""}`}
                    onClick={() => setViewMode("list")}
                    aria-label={t("store.viewList", { defaultValue: "List view" })}
                    title={t("store.viewList", { defaultValue: "List view" })}
                  >
                    <i className="fa-solid fa-list" />
                  </button>
                  <button
                    type="button"
                    className={`store-category-view-btn${viewMode === "grid" ? " is-active" : ""}`}
                    onClick={() => setViewMode("grid")}
                    aria-label={t("store.viewGrid", { defaultValue: "Grid view" })}
                    title={t("store.viewGrid", { defaultValue: "Grid view" })}
                  >
                    <i className="fa-solid fa-th-large" />
                  </button>
                </div>
                <div className="store-category-sort">
                  <select
                    id="store-category-sort-select"
                    value={sortBy}
                    onChange={(e) => { setSortBy(e.target.value); setVisibleCount(24); }}
                    className="store-category-sort-select"
                    aria-label={t("store.sortBy", { defaultValue: "Sort by" })}
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {sortOptionLabel(opt.value)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="store-category-loading">
                <div className="store-loading-spinner" aria-hidden="true" />
              </div>
            ) : visibleProducts.length === 0 ? (
              <div className="store-category-empty">
                <p>{t("store.noProductsMatchFilters", { defaultValue: "No products match your filters." })}</p>
                <button
                  type="button"
                  className="store-empty-reset"
                  onClick={() => {
                    setPriceMin(priceBounds[0]);
                    setPriceMax(priceBounds[1]);
                    setSelectedColors([]);
                    setSelectedMaterials([]);
                    setVisibleCount(24);
                    if (hasSceneProductFilter) clearSceneProductFilter();
                  }}
                >
                  {t("store.clearFilters", { defaultValue: "Clear filters" })}
                </button>
              </div>
            ) : (
              <div
                className={`store-category-grid${viewMode === "list" ? " store-category-grid--list" : ""}`}
                role="list"
              >
                {visibleProducts.map((product, index) => (
                  <div
                    key={product._id}
                    className="store-category-grid-item store-weekly-card"
                    role="listitem"
                  >
                    <ProductCard
                      product={product}
                      onAddToCart={addToCart}
                      resolveUrl={resolveProductUrl}
                      animationDelay={index * 40}
                      onToggleWishlist={toggleWishlist}
                      isInWishlist={isInWishlist}
                      onQuickView={setQuickViewProduct}
                    />
                  </div>
                ))}
              </div>
            )}

            <div ref={loadMoreRef} className="store-category-loadmore" aria-hidden="true" />
            {!loading && visibleCount < totalResults ? (
              <div className="store-category-loadmore-hint">
                Loading more…
              </div>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          className="store-category-filter-pill"
          onClick={() => setMobileFiltersOpen(true)}
          aria-expanded={mobileFiltersOpen}
          aria-controls="store-category-mobile-filters-panel"
          aria-label={t("store.filtersOpenAria", { defaultValue: "Filters" })}
        >
          {activeFilterCount > 0 ? (
            <span className="store-category-filter-pill-badge" aria-hidden="true">
              {activeFilterCount > 99 ? "99+" : activeFilterCount}
            </span>
          ) : null}
          <span className="store-category-filter-pill-icon-wrap" aria-hidden="true">
            <i className="fa-solid fa-sliders" />
          </span>
        </button>

        {mobileFiltersOpen ? (
          <div className="store-category-mobile-filters" role="presentation">
            <button
              type="button"
              className="store-category-mobile-filters-backdrop"
              aria-label={t("store.filtersCloseAria", { defaultValue: "Close filters" })}
              onClick={() => setMobileFiltersOpen(false)}
            />
            <div
              id="store-category-mobile-filters-panel"
              className="store-category-mobile-filters-sheet"
              role="dialog"
              aria-modal="true"
              aria-labelledby="store-category-mobile-filters-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="store-category-mobile-filters-header">
                <h2 id="store-category-mobile-filters-title" className="store-category-mobile-filters-title">
                  {t("store.filtersSheetTitle", { defaultValue: "Filters" })}
                </h2>
                <button
                  type="button"
                  className="store-category-mobile-filters-close"
                  onClick={() => setMobileFiltersOpen(false)}
                  aria-label={t("store.filtersCloseAria", { defaultValue: "Close filters" })}
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
              </div>
              <div className="store-category-mobile-filters-body">{categoryFilterSections}</div>
            </div>
          </div>
        ) : null}
      </section>
      <QuickViewModal
        product={quickViewProduct}
        isOpen={!!quickViewProduct}
        onClose={() => setQuickViewProduct(null)}
        onAddToCart={addToCart}
        resolveUrl={resolveProductUrl}
      />
    </div>
    </>
  );
}
